import type { RGBColor } from "../types/colors/RGBColor"
import type { ColorComponent } from "../types/Units"

type HSLColor = {
  alpha: number
  hue: number
  lightness: number
  saturation: number
}

/**
 * Helper function to convert hue to RGB component
 */
const hueToRgb = (p: number, q: number, t: number): number => {
  if (t < 0) t += 1
  if (t > 1) t -= 1
  if (t < 1 / 6) return p + (q - p) * 6 * t
  if (t < 1 / 2) return q
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
  return p
}

/**
 * Converts HSL color to RGB color
 * Assumes HSL values are in range 0-1
 */
export const HslToRgb = ({
  alpha,
  hue,
  lightness,
  saturation,
}: HSLColor): RGBColor => {
  let red, green, blue

  if (saturation === 0) {
    // Achromatic (gray)
    red = green = blue = lightness
  } else {
    const q =
      lightness < 0.5
        ? lightness * (1 + saturation)
        : lightness + saturation - lightness * saturation
    const p = 2 * lightness - q

    red = hueToRgb(p, q, hue + 1 / 3)
    green = hueToRgb(p, q, hue)
    blue = hueToRgb(p, q, hue - 1 / 3)
  }

  return {
    alpha: alpha as ColorComponent,
    blue: blue as ColorComponent,
    green: green as ColorComponent,
    red: red as ColorComponent,
  }
}
